import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {NonEmptyString, StringUnion, EmptyString, isNonEmptyString} from 'Core/models/EmptyString';
import {
  SocialSecurityNumber,
  ValidSocialSecurityNumber,
  EmptySocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {PolicyNumber, UserAssignedPolicyNumber} from './PolicyNumber';
import {Async} from 'Core/models/Async';

export class InvalidPolicyRecord {
  public readonly key = 'InvalidPolicyRecord';

  constructor(
    public readonly businessArea: StringUnion,
    public readonly status: StringUnion,
    public readonly workType: StringUnion,
    public readonly coCode: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly bankNumber: StringUnion,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly agent: StringUnion,
    public readonly caseNumber: StringUnion,
    public readonly priorityIncrease: number,
    public readonly stateSigned: StringUnion,
    public readonly payorNumber: StringUnion
  ) {}
}

type PolicyRecordProps = {
  businessArea: StringUnion;
  status: StringUnion;
  workType: StringUnion;
  coCode: StringUnion;
  policyNumber: PolicyNumber;
  bankNumber: StringUnion;
  socialSecurityNumber: SocialSecurityNumber;
  firstName: StringUnion;
  lastName: StringUnion;
  agent: StringUnion;
  caseNumber: StringUnion;
  priorityIncrease: number;
  stateSigned: StringUnion;
  payorNumber: StringUnion;
};

export class ValidPolicyRecord {
  public static createFromApp({
    businessArea,
    status,
    workType,
    coCode,
    policyNumber,
    bankNumber,
    socialSecurityNumber,
    firstName,
    lastName,
    agent,
    caseNumber,
    priorityIncrease,
    stateSigned,
    payorNumber,
  }: PolicyRecordProps) {
    return this.create(
      businessArea,
      status,
      workType,
      coCode,
      policyNumber,
      bankNumber,
      socialSecurityNumber,
      firstName,
      lastName,
      agent,
      caseNumber,
      priorityIncrease,
      stateSigned,
      payorNumber
    );
  }

  public static create(
    businessArea: StringUnion,
    status: StringUnion,
    workType: StringUnion,
    coCode: StringUnion,
    policyNumber: PolicyNumber,
    bankNumber: StringUnion,
    socialSecurityNumber: SocialSecurityNumber,
    firstName: StringUnion,
    lastName: StringUnion,
    agent: StringUnion,
    caseNumber: StringUnion,
    priorityIncrease: number,
    stateSigned: StringUnion,
    payorNumber: StringUnion
  ) {
    return isNonEmptyString(businessArea) &&
      isNonEmptyString(status) &&
      isNonEmptyString(workType) &&
      isNonEmptyString(agent)
      ? new ValidPolicyRecord(
          businessArea,
          status,
          workType,
          coCode,
          policyNumber,
          bankNumber,
          socialSecurityNumber,
          firstName,
          lastName,
          agent,
          caseNumber,
          priorityIncrease,
          stateSigned,
          payorNumber
        )
      : new InvalidPolicyRecord(
          businessArea,
          status,
          workType,
          coCode,
          policyNumber,
          bankNumber,
          socialSecurityNumber,
          firstName,
          lastName,
          agent,
          caseNumber,
          priorityIncrease,
          stateSigned,
          payorNumber
        );
  }

  public readonly key = 'ValidPolicyRecord';
  private constructor(
    public readonly businessArea: StringUnion,
    public readonly status: StringUnion,
    public readonly workType: StringUnion,
    public readonly coCode: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly bankNumber: StringUnion,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly agent: StringUnion,
    public readonly caseNumber: StringUnion,
    public readonly priorityIncrease: number,
    public readonly stateSigned: StringUnion,
    public readonly payorNumber: StringUnion
  ) {}
}

export type PolicyRecord = InvalidPolicyRecord | ValidPolicyRecord;

export type PolicyRecordOptions<TReturn> = ToPartialMatchOptions<PolicyRecord, 'key', TReturn>;

export const matchPolicyRecord = <TReturn>(options: PolicyRecordOptions<TReturn>) => (app: PolicyRecord): TReturn => {
  switch (app.key) {
    case 'InvalidPolicyRecord':
      return options.InvalidPolicyRecord ? options.InvalidPolicyRecord(app) : options.default(app);
    case 'ValidPolicyRecord':
      return options.ValidPolicyRecord ? options.ValidPolicyRecord(app) : options.default(app);
  }
};

export const EmptyPolicyRecord = new InvalidPolicyRecord(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptySocialSecurityNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString()
);

export const changeBusinessArea = (businessArea: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    businessArea: NonEmptyString.create(businessArea),
  });
export const changeStatus = (status: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    status: NonEmptyString.create(status),
  });
export const changeWorkType = (workType: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    workType: NonEmptyString.create(workType),
  });
export const changeCoCode = (coCode: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    coCode: NonEmptyString.create(coCode),
  });
export const changePolicyNumber = (policyNumber: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    policyNumber: new UserAssignedPolicyNumber(policyNumber),
  });

export const changeAsyncPolicyNumber = (policyNumber: Async<string>) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    policyNumber: policyNumber,
  });
export const changeBankNumber = (bankNumber: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    bankNumber: NonEmptyString.create(bankNumber),
  });
export const changeSocialSecurityNumber = (socialSecurityNumber: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber),
  });
export const changeFirstName = (firstName: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    firstName: NonEmptyString.create(firstName),
  });
export const changeLastName = (lastName: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    lastName: NonEmptyString.create(lastName),
  });
export const changeAgent = (agent: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    agent: NonEmptyString.create(agent),
  });
export const changeCaseNumber = (caseNumber: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    caseNumber: NonEmptyString.create(caseNumber),
  });
export const changePriorityIncrease = (priorityIncrease: number) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    priorityIncrease: priorityIncrease,
  });
export const changeStateSigned = (stateSigned: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    stateSigned: NonEmptyString.create(stateSigned),
  });
export const changePayorNumber = (payorNumber: string) => (app: PolicyRecord) =>
  ValidPolicyRecord.createFromApp({
    ...app,
    payorNumber: NonEmptyString.create(payorNumber),
  });
